import {
  AsyncError,
  AsyncResult,
  Conflict,
  NoContent,
  NoData,
  NotFound,
  ServerError,
  Unauthorized,
} from 'Core/models/Async';
import PromiseWithCancellation from 'Core/models/PromiseWithCancellation';
import {getAccessToken} from 'Shared/utilities/Auth';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import axiosInstance from 'Shared/utilities/axiosInstance';
import {AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';
import {saveAs} from 'file-saver';
import * as E from 'fp-ts/lib/Either';
import {getOrElse} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {CANCEL} from 'redux-saga';

export const enum RequestMethod {
  GET,
  POST,
}

type FileResponse = {
  data: Blob;
  contentdisposition: string;
};

export const getAxiosConfig = (cancelToken?: CancelToken, isFile?: boolean, noAuth?: boolean): AxiosRequestConfig => {
  const authHeader = noAuth
    ? undefined
    : pipe(
        getAccessToken(),
        getOrElse(() => '')
      );

  return {
    cancelToken,
    responseType: isFile ? 'blob' : undefined,
    paramsSerializer: params => addFilterParameters(params).slice(1),
    headers: {
      ...(authHeader !== undefined && {Authorization: authHeader}),
      'Cache-Control': 'no-store, no-cache, must-revalidate',
      Pragma: 'no-cache',
    },
  };
};

export const getUrlFromUri = (uri: string) => `${uri}`;

export const handleResponse = <TResponse>(response: AxiosResponse<TResponse>): AsyncResult<TResponse> => {
  if (response) {
    return response.status === 200 || response.status === 201
      ? E.right(response.data)
      : [204].includes(response.status)
      ? E.left(new NoContent(response.data))
      : [401, 403].includes(response.status)
      ? E.left(new Unauthorized())
      : [400].includes(response.status)
      ? E.left(response.data ? new NoData(response.data) : new NoData(response.headers?.badrequestexception))
      : [404].includes(response.status)
      ? E.left(new NotFound())
      : [409].includes(response.status)
      ? E.left(new Conflict(response.data))
      : E.left(new ServerError(response.data));
  } else {
    return E.left(new ServerError());
  }
};

export const handleFileResponse = <TFileResponse>(response: AxiosResponse<TFileResponse>) => {
  if (response) {
    let model = {
      data: response.data as unknown as Blob,
      contentdisposition: response.headers['content-disposition'],
    };

    return response.status === 200 || response.status === 201 ? E.right(model) : E.left(new ServerError(model.data));
  } else {
    return E.left(new ServerError());
  }
};

type CreatePromiseFunc<TReturn> = (cancelToken: CancelToken) => Promise<TReturn>;
export const withCancellation = <T>(createPromise: CreatePromiseFunc<T>) => {
  const source = axiosInstance.CancelToken.source();
  const promise = PromiseWithCancellation.resolve<T>(createPromise(source.token));
  promise[CANCEL] = () => source.cancel();

  return promise;
};

export const get = <TResponse>(uri: string, isFile?: boolean, noAuth?: boolean) =>
  withCancellation(cancelToken =>
    axiosInstance.get<TResponse>(getUrlFromUri(uri), getAxiosConfig(cancelToken, isFile, noAuth)).then(handleResponse)
  );

export const getFile = <TResponse>(uri: string, isFile?: boolean, noAuth?: boolean) =>
  withCancellation(cancelToken =>
    axiosInstance
      .get<TResponse>(getUrlFromUri(uri), getAxiosConfig(cancelToken, isFile, noAuth))
      .then(handleFileResponse)
  );

export const postFile = <TResponse>(uri: string, payload?: any, isFile?: boolean, noAuth?: boolean) => {
  return withCancellation(cancelToken =>
    axiosInstance
      .post<TResponse>(getUrlFromUri(uri), payload, getAxiosConfig(cancelToken, isFile, noAuth))
      .then(handleFileResponse)
  );
};

export const post = <TResponse>(uri: string, payload?: any, isFile?: boolean, noAuth?: boolean) => {
  return withCancellation(cancelToken =>
    axiosInstance
      .post<TResponse>(getUrlFromUri(uri), payload, getAxiosConfig(cancelToken, isFile, noAuth))
      .then(handleResponse)
  );
};

export const openFile = async (
  uri: string,
  fileExtension?: string,
  successResponse?: string,
  requestMethod?: RequestMethod,
  requestPayload?: any
) => {
  let response: E.Left<ServerError> | E.Right<{data: Blob; contentdisposition: string}>;
  if (requestMethod === RequestMethod.POST) {
    response = await postFile<Response>(uri, requestPayload, true);
  } else {
    response = await getFile<Response>(removePrefixFromLink(uri), true);
  }

  if (E.isRight(response)) {
    return ((fileResponse: FileResponse) => {
      let mimeType = fileResponse.data?.type;
      let fileName = fileResponse.contentdisposition;

      if (mimeType === 'application/vnd.ms-excel')
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      let blob = new Blob([fileResponse.data], {type: mimeType});
      let url = window.URL.createObjectURL(blob);
      if (fileResponse.data?.type == 'application/pdf' || fileExtension == 'pdf') {
         blob = new Blob([fileResponse.data], {type: 'application/pdf'});
         url = window.URL.createObjectURL(blob);
         window.open(url);
      } else {
        fileName = fileName
          .replace('attachment; filename=', '')
          .split('"')
          .join('');
        saveAs(blob, fileName);
      }
      return successResponse ? successResponse : 'The download was successful.';
    })(response.right);
  } else {
    return response.left;
  }
};

export const downloadFile = async (
  uri: string,
  downloadName: string,
  successResponse?: string,
  requestMethod?: RequestMethod,
  requestPayload?: any
) => {
  let response: E.Left<AsyncError> | E.Right<string>;
  if (requestMethod === RequestMethod.POST) {
    response = await post<string>(removePrefixFromLink(uri), requestPayload, true);
  } else {
    response = await get<string>(removePrefixFromLink(uri), true);
  }

  if (E.isRight(response)) {
    return ((pdfResponse: string) => {
      const blob = new Blob([pdfResponse]);
      saveAs(blob, downloadName);

      return successResponse ? successResponse : 'The download was successful.';
    })(response.right);
  } else {
    return response.left;
  }
};

export const removePrefixFromLink = (uri: string) => uri.replace('api/v1/', '');

export const postMultipart = <TResponse>(uri: string, payload: Blob, cancelTokenInput?: CancelToken) => {
  const data = new FormData();
  data.append('file', payload);

  return withCancellation(cancelToken =>
    axiosInstance
      .post<TResponse>(getUrlFromUri(uri), data, {
        ...getAxiosConfig(cancelTokenInput ? cancelTokenInput : cancelToken),
        headers: {
          ...getAxiosConfig(cancelToken).headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(handleResponse)
  );
};

export const put = <TResponse>(uri: string, payload?: any) =>
  withCancellation(cancelToken =>
    axiosInstance.put<TResponse>(getUrlFromUri(uri), payload, getAxiosConfig(cancelToken)).then(handleResponse)
  );
export const remove = (uri: string) => axiosInstance.delete(getUrlFromUri(uri), getAxiosConfig()).then(handleResponse);
